import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, ClickAwayListener, IconButton, makeStyles } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'
import dayjs from 'dayjs'
import { useToggle } from '../../../../hooks'
import { clearSuppression, setSuppression } from '../../../../utils/globalSuppression'
import { BUTTON_SIZES, BUTTON_VARIANT, ICON_NAMES } from '../../../../constants'
import Icon from '../../../atoms/Icon'
import Text from '../../../atoms/Text'
import RoundedButton from '../../../atoms/RoundedButton'
import Skeleton from '../../../atoms/Skeleton'
import TableSkeleton from '../../../atoms/TableSkeleton'
import InputInlineEdit from '../../../molecules/InputInlineEdit'
import { createWealthJourneyEntry, deleteWealthJourneyEntry, editWealthJourneyEntry } from '../../../../service'
import { entryTypeIdentifier } from '../../WealthJourney/v2/entryType'

const useStyles = makeStyles((theme) => ({
  container: ({ activitiesEmpty }) => ({
    height: '24rem',
    width: '100%',
    border: '2px solid #21294533',
    borderRadius: 'var(--border-radius-lg)',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    ...(activitiesEmpty ? {} : { cursor: 'pointer' })
  }),
  title: {
    display: 'flex',
    direction: 'row',
    alignItems: 'center',
    fontSize: '1.5rem',
    fontWeight: '700',
    marginBottom: '1.5rem'
  },
  content: ({ editMode }) => ({
    fontSize: '1rem',
    fontWeight: '400',
    lineHeight: '1.6',
    overflowY: 'auto',
    height: '100%',
    ...(editMode ? { marginRight: '-1.5rem' } : {})
  }),
  addNoteSection: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    borderRadius: 'var(--border-radius-lg)',
    justifyContent: 'center',
    flexDirection: 'column',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-300)'
    }
  },
  listBullet: {
    height: '0.4rem',
    width: '0.4rem',
    marginTop: '0.75rem',
    marginRight: '0.75rem',
    borderRadius: 'var(--border-radius-round)',
    backgroundColor: 'var(--color-neutral-700)'
  },
  textFieldContainer: ({ editMode }) => ({
    width: '100%',
    overflowWrap: 'anywhere',
    ...(editMode ? {
      borderBottom: '1px dashed #dddddd',
      marginRight: '1rem'
    } : {
      marginBottom: '0.25rem',
      paddingTop: '0.25rem'
    })
  }),
  textFieldContent: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row'
  },
  inlineEditTextField: {
    margin: 0,
    padding: 0,
    '& input': {
      minHeight: '2rem',
      fontSize: '0.875rem'
    }
  },
  inlineEditTextAdornmentClassName: {
    height: 'initial',
    maxHeight: 'unset'
  },
  entryItemContainer: {
    display: 'flex',
    flexDirection: 'row'
  },
  icon: {
    fontSize: '1rem !important'
  }
}))

// Individual activity entry component
const ActivityEntry = ({
  entry,
  editMode,
  canEdit,
  onEdit,
  onDelete,
  onCancel,
  classes
}) => {
  const [entryText, setEntryText] = useState(entry?.entryText || '')
  const [editInlineMode, setEditInlineMode] = useState(false)

  const handleChange = useCallback((e) => {
    setEntryText(e.target.value)
  }, [])

  const handleConfirm = useCallback(async (value) => {
    const normalizedValue = value.trim()
    if (entry?.isNewEntry) {
      await onEdit(normalizedValue, true)
    } else {
      await onEdit(entry.entryId, normalizedValue)
    }
  }, [entry, onEdit])

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel()
    }
    setEntryText(entry?.entryText || '')
  }, [entry, onCancel])

  const handleDelete = useCallback(() => {
    onDelete(entry.entryId)
  }, [entry.entryId, onDelete])

  return (
    <div className={classes.entryItemContainer}>
      {(!editMode || !canEdit) && <div className={classes.listBullet} />}
      <div className={classes.textFieldContainer}>
        {editMode && canEdit ? (
          <div className={classes.textFieldContent}>
            <InputInlineEdit
              width='100%'
              autoFocus
              multiline
              showEditIcon
              disableUnderline
              alwaysShowEditButton
              value={entryText}
              initInEditMode={entry?.isNewEntry}
              onIconClickHandler={handleConfirm}
              onChangeHandler={handleChange}
              onToggleEditMode={setEditInlineMode}
              onCancelIconClickHandler={handleCancel}
              textFieldClassName={classes.inlineEditTextField}
              textFieldAdornmentClassName={classes.inlineEditTextAdornmentClassName}
            />
            {!editInlineMode && (
              <IconButton onClick={handleDelete}>
                <DeleteIcon className={classes.icon} />
              </IconButton>
            )}
          </div>
        ) : (
          <Text customFontSize='1rem' text={entry.entryText} />
        )}
      </div>
    </div>
  )
}

ActivityEntry.propTypes = {
  entry: PropTypes.object.isRequired,
  editMode: PropTypes.bool,
  canEdit: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  classes: PropTypes.object.isRequired
}

/**
 * DDPWealthJourneyCategory - Display and manage category activities
 *
 * Features:
 * - Accepts pre-filtered activities or filters from all entries
 * - Add/Edit/Delete activities
 * - Self-contained CRUD operations
 * - Edit mode with inline editing
 * - Empty state with add functionality
 *
 * @component
 */
const DDPWealthJourneyCategory = ({
  title,
  categoryId,
  activities,
  allEntries,
  entryDateYear,
  isLoading,
  canEdit,
  clientId,
  onActivitiesUpdated,
  className,
  style
}) => {
  const [hovered, , toggleHoveredOn, toggleHoveredOff] = useToggle()
  const [editMode, toggleEditMode, toggleEditModeOn, toggleEditModeOff] = useToggle()
  const [placeholders, setPlaceholders] = useState([])
  const [localActivities, setLocalActivities] = useState([])

  // Filter activities if allEntries provided, otherwise use activities prop
  const filteredActivities = useMemo(() => {
    if (activities) return activities

    if (!allEntries) return []

    return allEntries.filter(entry => {
      return (
        (entry.entryTypeId === null || entry.entryTypeId === entryTypeIdentifier.ACTIVITIES) &&
        entry.categoryId === categoryId &&
        dayjs.utc(entry.entryDate).year() === entryDateYear
      )
    })
  }, [activities, allEntries, categoryId, entryDateYear])

  // Initialize local activities
  useEffect(() => {
    setLocalActivities(filteredActivities)
  }, [filteredActivities])

  const activitiesEmpty = useMemo(
    () => !isLoading && isEmpty(localActivities) && isEmpty(placeholders),
    [localActivities, placeholders, isLoading]
  )

  const classes = useStyles({ activitiesEmpty, editMode })

  // Manage wheel suppression
  useEffect(() => {
    if (editMode) {
      setSuppression('wheel', true)
      return () => {
        clearSuppression('wheel')
      }
    }
  }, [editMode])

  // Add placeholder
  const addPlaceholder = useCallback(() => {
    if (!editMode) toggleEditModeOn()
    setPlaceholders(prev => ([
      ...prev,
      { entryId: null, entryText: '', isNewEntry: true }
    ]))
  }, [editMode, toggleEditModeOn])

  // Remove placeholders
  const removePlaceholders = useCallback(() => {
    setPlaceholders([])
  }, [])

  // Create new activity
  const handleAddActivity = useCallback(async (entryText, isNew) => {
    if (!isNew) return

    try {
      const payload = {
        clientId,
        entryText,
        categoryId,
        entryDate: dayjs().year(entryDateYear).toISOString(),
        entryTypeId: entryTypeIdentifier.ACTIVITIES
      }

      const { data } = await createWealthJourneyEntry(payload)
      const { entryId } = data

      // Add to local state
      const newEntry = { entryId, ...payload }
      setLocalActivities(prev => [...prev, newEntry])
      removePlaceholders()

      // Notify parent
      if (onActivitiesUpdated) {
        onActivitiesUpdated('add', newEntry)
      }
    } catch (err) {
      console.error('Error creating activity:', err)
    }
  }, [clientId, categoryId, entryDateYear, removePlaceholders, onActivitiesUpdated])

  // Update activity
  const handleEditActivity = useCallback(async (entryId, entryText) => {
    try {
      await editWealthJourneyEntry(entryId, { entryText })

      // Update local state
      setLocalActivities(prev =>
        prev.map(a => a.entryId === entryId ? { ...a, entryText } : a)
      )

      // Notify parent
      if (onActivitiesUpdated) {
        onActivitiesUpdated('edit', { entryId, entryText })
      }
    } catch (err) {
      console.error('Error updating activity:', err)
    }
  }, [onActivitiesUpdated])

  // Delete activity
  const handleDeleteActivity = useCallback(async (entryId) => {
    try {
      await deleteWealthJourneyEntry(entryId)

      // Remove from local state
      setLocalActivities(prev => prev.filter(a => a.entryId !== entryId))

      // Notify parent
      if (onActivitiesUpdated) {
        onActivitiesUpdated('delete', entryId)
      }
    } catch (err) {
      console.error('Error deleting activity:', err)
    }
  }, [onActivitiesUpdated])

  // Render header
  const showEditIcon = hovered && !editMode && !activitiesEmpty && !isLoading && canEdit

  const header = isLoading ? (
    <div className={classes.title}>
      <Skeleton height='2rem' width='100%' />
    </div>
  ) : (
    <div className={classes.title}>
      {title}
      {showEditIcon && (
        <Box ml='auto'>
          <IconButton aria-label='toggle edit mode' size='small' onClick={toggleEditMode}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Box>
      )}
    </div>
  )

  // Render empty state
  const emptyState = canEdit ? (
    <Box className={classes.addNoteSection} onClick={addPlaceholder}>
      <Box display='flex' mb='0.5rem'>
        <Icon name={ICON_NAMES.addCircle} customSize='1.5rem' />
      </Box>
      <Text text='Add note' customFontWeight='bold' />
    </Box>
  ) : null

  // Render body
  const body = isLoading ? (
    <TableSkeleton rows={4} columns={1} showHeader={false} />
  ) : activitiesEmpty ? (
    emptyState
  ) : (
    <>
      <div className={classes.content}>
        {localActivities.map(entry => (
          <ActivityEntry
            key={`activity-${entry.entryId}`}
            entry={entry}
            editMode={editMode}
            canEdit={canEdit}
            onEdit={handleEditActivity}
            onDelete={handleDeleteActivity}
            classes={classes}
          />
        ))}
        {placeholders.map((entry, idx) => (
          <ActivityEntry
            key={`placeholder-${idx}`}
            entry={entry}
            editMode={editMode}
            canEdit={canEdit}
            onEdit={handleAddActivity}
            onDelete={noop}
            onCancel={removePlaceholders}
            classes={classes}
          />
        ))}
      </div>
      {canEdit && editMode && (
        <Box mt='1rem' alignSelf='start'>
          <RoundedButton
            size={BUTTON_SIZES.extraSmall}
            variant={BUTTON_VARIANT.ghost}
            onClick={addPlaceholder}
            disabled={!!placeholders.length}
          >
            <Box display='flex' mr='0.25rem'>
              <Icon name={ICON_NAMES.addCircle} customSize='1rem' />
            </Box>
            <Text customFontWeight='bold' lineHeight='inherit' text='Add note' />
          </RoundedButton>
        </Box>
      )}
    </>
  )

  return (
    <ClickAwayListener onClickAway={toggleEditModeOff}>
      <div
        className={`${classes.container} ${className}`}
        style={style}
        onClick={activitiesEmpty ? noop : toggleEditModeOn}
        onMouseEnter={toggleHoveredOn}
        onMouseLeave={toggleHoveredOff}
      >
        {header}
        {body}
      </div>
    </ClickAwayListener>
  )
}

DDPWealthJourneyCategory.propTypes = {
  /** Category title */
  title: PropTypes.string.isRequired,

  /** Category ID */
  categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /** Pre-filtered activities for this category (optional) */
  activities: PropTypes.array,

  /** All entries to filter from (if activities not provided) */
  allEntries: PropTypes.array,

  /** Year to filter activities by */
  entryDateYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  /** Loading state */
  isLoading: PropTypes.bool,

  /** Whether user can edit activities */
  canEdit: PropTypes.bool,

  /** Client ID for creating new activities */
  clientId: PropTypes.string,

  /** Callback when activities are updated */
  onActivitiesUpdated: PropTypes.func,

  /** Additional CSS class */
  className: PropTypes.string,

  /** Inline styles */
  style: PropTypes.object
}

DDPWealthJourneyCategory.defaultProps = {
  activities: null,
  allEntries: null,
  isLoading: false,
  canEdit: false,
  clientId: null,
  onActivitiesUpdated: null,
  className: '',
  style: {}
}

export default DDPWealthJourneyCategory
